// Standardized responsive breakpoints for Tenant Portal
export const RESPONSIVE_BREAKPOINTS = {
  // Mobile breakpoint - 0 to 767px
  mobile: {
    min: 0,
    max: 767,
    query: '(max-width: 767px)',
  },
  
  // Tablet breakpoint - 768px to 1024px
  tablet: {
    min: 768,
    max: 1024,
    query: '(min-width: 768px) and (max-width: 1024px)',
  },
  
  // Laptop L breakpoint - 1025px to 1574px
  laptopL: {
    min: 1025,
    max: 1574,
    query: '(min-width: 1025px) and (max-width: 1574px)',
  },
  
  // Desktop XL breakpoint - 1575px and above
  desktopXL: {
    min: 1575,
    max: Infinity,
    query: '(min-width: 1575px)',
  },

  // Desktop XXL breakpoint - 1619px and above
  desktopXXL: {
    min: 1619,
    max: Infinity,
    query: '(min-width: 1619px)',
  },
  
  // Desktop breakpoint - 1025px and above (legacy)
  desktop: {
    min: 1025,
    max: Infinity,
    query: '(min-width: 1025px)',
  },

  // Legacy breakpoints for backwards compatibility
  legacy: {
    smallScreen: '(max-width: 744px)',
    mediumScreen: '(max-width: 834px)',
    largeScreen: '(min-width: 960px)',
    extraLarge: '(min-width: 1574px)',
  }
}

// Media query helpers
export const createMediaQuery = (breakpoint, condition = 'min') => {
  const bp = RESPONSIVE_BREAKPOINTS[breakpoint]
  if (!bp) return ''
  
  if (condition === 'min') {
    return `(min-width: ${bp.min}px)`
  } else if (condition === 'max') {
    return `(max-width: ${bp.max}px)`
  }
  
  return bp.query
}

// CSS-in-JS media query helpers
export const mediaQueries = {
  mobile: `@media ${RESPONSIVE_BREAKPOINTS.mobile.query}`,
  tablet: `@media ${RESPONSIVE_BREAKPOINTS.tablet.query}`,
  laptopL: `@media ${RESPONSIVE_BREAKPOINTS.laptopL.query}`,
  desktopXL: `@media ${RESPONSIVE_BREAKPOINTS.desktopXL.query}`,
  desktopXXL: `@media ${RESPONSIVE_BREAKPOINTS.desktopXXL.query}`,
  desktop: `@media ${RESPONSIVE_BREAKPOINTS.desktop.query}`,
  smallScreen: `@media ${RESPONSIVE_BREAKPOINTS.legacy.smallScreen}`,
  
  // Helper functions for inline usage
  above: (breakpoint) => `@media (min-width: ${RESPONSIVE_BREAKPOINTS[breakpoint]?.min || 0}px)`,
  below: (breakpoint) => `@media (max-width: ${RESPONSIVE_BREAKPOINTS[breakpoint]?.max || 9999}px)`,
  between: (minBp, maxBp) => {
    const min = RESPONSIVE_BREAKPOINTS[minBp]?.min || 0
    const max = RESPONSIVE_BREAKPOINTS[maxBp]?.max || 9999
    return `@media (min-width: ${min}px) and (max-width: ${max}px)`
  }
}

// Component-specific breakpoints
export const COMPONENT_BREAKPOINTS = {
  topNavigation: {
    mobile: RESPONSIVE_BREAKPOINTS.mobile.query,
    tablet: RESPONSIVE_BREAKPOINTS.tablet.query,
    laptopL: RESPONSIVE_BREAKPOINTS.laptopL.query,
    desktopXL: RESPONSIVE_BREAKPOINTS.desktopXL.query,
    desktopXXL: RESPONSIVE_BREAKPOINTS.desktopXXL.query,
    desktop: RESPONSIVE_BREAKPOINTS.desktop.query,
  },
  
  sidebar: {
    mobile: RESPONSIVE_BREAKPOINTS.mobile.query,
    tablet: RESPONSIVE_BREAKPOINTS.tablet.query,
    laptopL: RESPONSIVE_BREAKPOINTS.laptopL.query,
    desktopXL: RESPONSIVE_BREAKPOINTS.desktopXL.query,
    desktopXXL: RESPONSIVE_BREAKPOINTS.desktopXXL.query,
    desktop: RESPONSIVE_BREAKPOINTS.desktop.query,
  },
  
  pageShell: {
    mobile: RESPONSIVE_BREAKPOINTS.mobile.query,
    tablet: RESPONSIVE_BREAKPOINTS.tablet.query,
    laptopL: RESPONSIVE_BREAKPOINTS.laptopL.query,
    desktopXL: RESPONSIVE_BREAKPOINTS.desktopXL.query,
    desktopXXL: RESPONSIVE_BREAKPOINTS.desktopXXL.query,
    desktop: RESPONSIVE_BREAKPOINTS.desktop.query,
  }
}

// Component dimension constants
export const DIMENSIONS = {
  // Sidebar widths
  SIDEBAR_WIDTH_MOBILE: 280,
  SIDEBAR_WIDTH_TABLET: 280,
  SIDEBAR_WIDTH_DESKTOP: 352,

  // Payments layout widths
  AUTOPAY_TOTAL_MAX_WIDTH: 860,
  
  // Modal and component dimensions
  MODAL_HEIGHT_STANDARD: 280,
  PROPERTY_CARD_WIDTH: 352,
  USER_CARD_WIDTH: 397,
  
  // Minimum component sizes
  MIN_CARD_WIDTH: 280,
  MIN_GRID_ITEM_WIDTH: 280,
}

// Dimension helpers with px suffix
export const DIMENSIONS_PX = {
  SIDEBAR_WIDTH_MOBILE: `${DIMENSIONS.SIDEBAR_WIDTH_MOBILE}px`,
  SIDEBAR_WIDTH_TABLET: `${DIMENSIONS.SIDEBAR_WIDTH_TABLET}px`,
  SIDEBAR_WIDTH_DESKTOP: `${DIMENSIONS.SIDEBAR_WIDTH_DESKTOP}px`,
  AUTOPAY_TOTAL_MAX_WIDTH: `${DIMENSIONS.AUTOPAY_TOTAL_MAX_WIDTH}px`,
  MODAL_HEIGHT_STANDARD: `${DIMENSIONS.MODAL_HEIGHT_STANDARD}px`,
  PROPERTY_CARD_WIDTH: `${DIMENSIONS.PROPERTY_CARD_WIDTH}px`,
  USER_CARD_WIDTH: `${DIMENSIONS.USER_CARD_WIDTH}px`,
  MIN_CARD_WIDTH: `${DIMENSIONS.MIN_CARD_WIDTH}px`,
  MIN_GRID_ITEM_WIDTH: `${DIMENSIONS.MIN_GRID_ITEM_WIDTH}px`,
}

export default RESPONSIVE_BREAKPOINTS
